import { ref, watch, type Ref } from 'vue';
import { logger } from '@/utils/logger';

export interface SummaryPollingOptions {
  interval?: number;
  timeout?: number;
}

export function useSummaryPolling(
  extractionsDone: Ref<boolean>,
  hasSummary: Ref<boolean>,
  pollFn: () => Promise<void>,
  options: SummaryPollingOptions = {}
): { isPolling: Ref<boolean>; start: () => void; stop: () => void } {
  const { interval = 5000, timeout = 45000 } = options;

  const isPolling = ref(false);
  let startTime = 0;
  let timer: number | null = null;

  const stop = (): void => {
    isPolling.value = false;
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  };

  const start = (): void => {
    if (isPolling.value) return;

    isPolling.value = true;
    startTime = Date.now();

    timer = window.setInterval(async () => {
      if (!extractionsDone.value || hasSummary.value) {
        stop();
        return;
      }

      if (Date.now() - startTime >= timeout) {
        stop();
        return;
      }

      try {
        await pollFn();
      } catch (error) {
        logger.error('Error during summary polling:', error);
        stop();
      }
    }, interval);
  };

  // Watch for when extractions are complete but summary is not
  watch([extractionsDone, hasSummary], ([extractionsDone, summaryAvailable]) => {
    if (extractionsDone && !summaryAvailable && !isPolling.value) {
      // Start polling for summary
      start();
    } else if (summaryAvailable && isPolling.value) {
      // Stop polling if summary is now available
      stop();
    }
  });

  // Return the polling state and manual control functions
  return {
    isPolling,
    start,
    stop,
  };
}
